import srraf from "srraf"
import getMediaQuery from "../lib/media-queries.js"
import debounce from "@/utils/debounce.js"
import EVENTS from "../lib/events.js"

const {
  ANNOUNCEMENT_HEIGHT_UPDATED,
  SHOPIFY_BLOCK_SELECT,
  SHOPIFY_BLOCK_DESELECT,
} = EVENTS

// regex matches a floating number from the media query string
const breakpointRegex = /([0-9.]+)/g

class AnnouncementBar extends HTMLElement {
  static observedAttributes = ["data-style"]

  constructor() {
    super()

    this.debounce = debounce()

    this.selectors = {
      announcementSlider: "announcement-slider",
    }
  }

  connectedCallback() {
    this.eventController = new AbortController()
    const eventOptions = { signal: this.eventController.signal }

    this.parentElement.addEventListener(
      SHOPIFY_BLOCK_SELECT,
      (e) => this.handleBlockSelected(e.detail.blockId),
      eventOptions,
    )

    this.parentElement.addEventListener(
      SHOPIFY_BLOCK_DESELECT,
      () => this.handleBlockDeselected(),
      eventOptions,
    )

    this.setHeightVariable()
    this.stickIfNecessary()
    this.widthWatcher = srraf(({ vw, pvw }) => {
      if (vw === pvw) return
      this.debounce(() => {
        this.setHeightVariable()
        this.stickIfNecessary()
      })
    })
  }

  disconnectedCallback() {
    this.eventController.abort()
    this.widthWatcher?.destroy()
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    // if announcement style is changed to slideshow, track it
    if (name === "data-style") {
      if (newValue === "slideshow") {
        this.sliderComponent = this.querySelector(
          this.selectors.announcementSlider,
        )
      } else {
        this.sliderComponent = null
      }
    }
  }

  stickIfNecessary() {
    const stickyCondition = this.dataset.enableStickyAnnouncementBar
    if (stickyCondition === "never") return

    const setSticky = (bool) => this.toggleAttribute("data-sticky", bool)
    const breakpoint = parseFloat(
      getMediaQuery("mobile").match(breakpointRegex)[0],
    )

    switch (stickyCondition) {
      case "desktop-and-mobile":
        setSticky(true)
        break
      case "desktop":
        setSticky(window.innerWidth >= breakpoint)
        break
      case "mobile":
        setSticky(window.innerWidth < breakpoint)
        break
    }
  }

  /**
   * Pauses the slider and changes to the selected block's slide.
   * @param {String} blockId - The block ID for the selected block.
   */
  handleBlockSelected(blockId) {
    this.setSliderAutoplay("false")
    this.setSliderActiveBlock(blockId)
  }

  /**
   * Resumes the slider and resets the selected block.
   */
  handleBlockDeselected() {
    this.setSliderAutoplay("true")
    this.setSliderActiveBlock("")
  }

  /**
   * Sets the slider's autoplay attribute value.
   * @param {String} value - The autoplay state, as either "true" or "false".
   */
  setSliderAutoplay(value) {
    if (this.sliderComponent) {
      this.sliderComponent.dataset.autoplay = value
    }
  }

  /**
   * Sets the slider's active block id.
   * @param {String} blockId - The block ID for the selected block.
   */
  setSliderActiveBlock(blockId) {
    if (this.sliderComponent) {
      this.sliderComponent.dataset.activeBlockId = blockId
    }
  }

  /**
   * Measures the announcement bar's height and applies that value to a document CSS variable.
   */
  setHeightVariable() {
    // We have to use a high-resolution height value to avoid rounding errors
    // this.offsetHeight rounds to 37px where getBoundingClientRect().height is 36.578125px
    // this fixes the browser rendering a 1px gap between the announcement bar and the header
    const height = this.getBoundingClientRect().height.toFixed(2)

    if (height !== this.lastSetHeight) {
      document.documentElement.style.setProperty(
        "--announcement-height",
        `${height}px`,
      )
      document.dispatchEvent(
        new CustomEvent(ANNOUNCEMENT_HEIGHT_UPDATED, {
          detail: { height },
        }),
      )
      this.lastSetHeight = height
    }
  }
}

if (!customElements.get("announcement-bar")) {
  customElements.define("announcement-bar", AnnouncementBar)
}
